import { Camera, Navigation, PenLine, Phone } from 'lucide-react';
import { Banner, BigButton, Card, Empty, Label, LinkButton, Screen } from '../components/ui';
import { eventLabel } from '../lib/actions';
import { db, useLive } from '../lib/db';
import { dayTime, telHref, timeOf } from '../lib/format';
import { navigationLinks } from '../lib/geo';
import { countryName } from '../lib/labels';
import { navigate } from '../lib/router';
import { useStop, useTrip } from '../lib/selectors';

/** Пълните данни за един адрес. Отваря се, когато „Днес" не е достатъчно. */
export function StopScreen({ stopId }: { stopId: string }) {
  const stop = useStop(stopId);
  const trip = useTrip(stop?.tripId ?? null);
  const events = useLive(
    async () => {
      const rows = await db.events.where('orderStopId').equals(stopId).toArray();
      return rows.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
    },
    [stopId],
    [],
  );

  if (!stop) {
    return (
      <Screen title="Адрес" back>
        <Empty>Този адрес вече не е в курса.</Empty>
      </Screen>
    );
  }

  const links = navigationLinks({
    lat: stop.lat,
    lon: stop.lon,
    address: stop.address,
    city: stop.city,
    country: stop.country,
  });
  const siteTel = telHref(stop.contactPhone);
  const dispatcherTel = telHref(trip?.dispatcher?.phone ?? null);

  return (
    <Screen title={stop.kind === 'pickup' ? 'Товарене' : 'Разтоварване'} back>
      <div className="space-y-4">
        <Card className="!bg-paper">
          <h2 className="text-3xl font-bold leading-tight">{stop.name}</h2>
          <p className="mt-1 text-2xl">{stop.address}</p>
          <p className="text-2xl">
            {stop.postcode ? `${stop.postcode} ` : ''}
            {stop.city}, {countryName(stop.country)}
          </p>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <LinkButton href={links.google} icon={<Navigation size={26} />}>
            Google Maps
          </LinkButton>
          <LinkButton href={links.waze} icon={<Navigation size={26} />}>
            Waze
          </LinkButton>
        </div>

        <Card>
          <Label>Час на място</Label>
          <p className="text-2xl font-semibold">
            {stop.windowFrom ? dayTime(stop.windowFrom) : 'без определен час'}
            {stop.windowTo ? ` – ${timeOf(stop.windowTo)}` : ''}
          </p>
          {stop.isHardDeadline ? (
            <p className="mt-1 text-lg font-bold text-stop">Краен срок — закъснение спира линия.</p>
          ) : null}
        </Card>

        {stop.contactName || siteTel ? (
          <Card>
            <Label>Лице за контакт</Label>
            <p className="text-2xl font-semibold">{stop.contactName ?? '—'}</p>
            {siteTel ? (
              <LinkButton href={siteTel} icon={<Phone size={26} />} tone="brand">
                {stop.contactPhone}
              </LinkButton>
            ) : null}
          </Card>
        ) : null}

        {stop.reference ? (
          <Card>
            <Label>Референция / номер на доставка</Label>
            <p className="text-2xl font-semibold">{stop.reference}</p>
          </Card>
        ) : null}

        {stop.goodsDescription || stop.weightKg ? (
          <Card>
            <Label>Товар</Label>
            <p className="text-xl">{stop.goodsDescription ?? '—'}</p>
            <p className="text-xl">
              {stop.weightKg ? `${stop.weightKg} кг` : ''}
              {stop.packagesCount ? ` · ${stop.packagesCount} колета` : ''}
            </p>
          </Card>
        ) : null}

        {stop.instructions ? <Banner tone="warn">{stop.instructions}</Banner> : null}

        {stop.kind === 'pickup' ? (
          <BigButton
            icon={<PenLine size={26} />}
            tone={events.some((e) => e.type === 'ecmr_signed') ? 'plain' : 'brand'}
            onClick={() => navigate(`/stop/${stop.id}/ecmr`)}
            hint={
              events.some((e) => e.type === 'ecmr_signed')
                ? 'Подписана — натиснете, за да подпишете отново'
                : 'Изпращачът и вие се подписвате на телефона'
            }
          >
            е-ЧМР: подписи на товарене
          </BigButton>
        ) : null}

        <BigButton icon={<Camera size={26} />} onClick={() => navigate(`/stop/${stop.id}/docs`)}>
          Документи
        </BigButton>

        {dispatcherTel ? (
          <LinkButton href={dispatcherTel} icon={<Phone size={26} />}>
            Обади се на диспечера
          </LinkButton>
        ) : null}

        <div>
          <Label>Какво е записано</Label>
          {events.length === 0 ? (
            <p className="mt-2 text-lg text-muted">Още няма записани действия за този адрес.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {events.map((event) => (
                <li
                  key={event.id}
                  className="flex items-center justify-between rounded-xl border-2 border-line px-3 py-2 text-lg"
                >
                  <span className="font-semibold">{eventLabel(event.type)}</span>
                  <span className="text-muted">
                    {timeOf(event.at)}
                    {event.synced === 0 ? ' · чака' : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Screen>
  );
}
